import {
  RuntimeData,
  StartChatInitialData,
} from '@wix/wix-chatbot-common/browser';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ACTIONS } from '../../constants';
import { applyUrlToNavigationHistory, appRoutes } from '../../react-router';
import { logger } from '../../util/logger';
import { getHelpWidgetSession } from '../api/getHelpWidgetSession';
import { updateHelpWidgetSession } from '../api/updateHelpWidgetSession';
import { clientSdk } from '../ClientSDK';
import { useFedopsLogger } from '../providers/fedopsLogger';
import { ISetSessionId } from '../reducers/types';
import { selectWidgetSessionState } from '../selectors/widgetSession';
import { helpCenterService } from '../services/helpCenter';
import { homepageService } from '../services/homepage/homepageService';
import { useGetHelpWidgetHistory } from './useGetHelpWidgetHistory';
import { useGetInitialChatbotState } from './useGetInitialChatbotState';

export const useGetRuntimeData = (): (() => Promise<RuntimeData>) => {
  const dispatch = useDispatch();
  const { sessionTabId } = useSelector(selectWidgetSessionState);
  const provideRuntimeDataFromSDK = helpCenterService.provideRuntimeDataFromSDK;
  const fedopsLogger = useFedopsLogger();
  const getHelpCenterHistory = useGetHelpWidgetHistory();
  const { getInitialChatbotState } = useGetInitialChatbotState();

  const getRuntimeDataFromSDK = useCallback(async (): Promise<RuntimeData> => {
    try {
      fedopsLogger.interactionStarted('fetching-widget-runtime-data');
      const runtimeData = await clientSdk.getRuntimeData();
      fedopsLogger.interactionEnded('fetching-widget-runtime-data');

      if (
        homepageService.homepageEnabled &&
        runtimeData.helpCenterSessionHistory.length === 0
      ) {
        // if navigation history is empty we need to update it with initial route(like we do in useGetHelpCenterHistory)
        // do this only when homepageEnabled to avoid impact on functionality we have now
        const session = await updateHelpWidgetSession({
          id: sessionTabId,
          addedUrl: appRoutes.home(),
        });
        const history = session?.navigationHistory ?? [appRoutes.home()];
        runtimeData.helpCenterSessionHistory.push(...history);
      }
      dispatch<ISetSessionId>({
        type: ACTIONS.SET_SESSION_ID,
        sessionId: runtimeData.widgetSession?.sessionId,
      });

      return runtimeData;
    } catch (error) {
      logger.log({
        message: 'Failed to get Runtime Data from SDK',
        error,
      });
      throw error;
    }
  }, [dispatch, fedopsLogger, sessionTabId]);

  const getRuntimeDataDefault = useCallback(async (): Promise<RuntimeData> => {
    const [
      startChat,
      helpCenterSessionHistory,
      widgetSession,
    ] = await Promise.all([
      getInitialChatbotState(),
      getHelpCenterHistory(),
      getHelpWidgetSession(),
    ]);

    return {
      helpCenterSessionHistory,
      startChatInitialData: startChat.initialState as StartChatInitialData,
      widgetSession: {
        sessionId: widgetSession?.sessionId || '',
        navigationHistory: helpCenterSessionHistory,
      },
    };
  }, [getInitialChatbotState, getHelpCenterHistory]);

  return useCallback(async (): Promise<RuntimeData> => {
    const runtimeData = provideRuntimeDataFromSDK
      ? await getRuntimeDataFromSDK()
      : await getRuntimeDataDefault();

    applyUrlToNavigationHistory(runtimeData?.helpCenterSessionHistory);

    return runtimeData;
  }, [getRuntimeDataFromSDK, getRuntimeDataDefault, provideRuntimeDataFromSDK]);
};
